import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

interface TabConfig {
  id: string;
  label: string;
  icon: React.ReactNode;
}

interface CustomContent {
  showTabs?: boolean;
  tabs?: TabConfig[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  customNode?: ReactNode; // For custom React components (e.g., translation pages) - left side
  rightSideButtons?: ReactNode; // For action buttons on the right side (e.g., translation controls)
}

interface PageTitleContextType {
  title: string;
  description?: string;
  customContent?: CustomContent;
  setPageTitle: (title: string, description?: string, customContent?: CustomContent) => void;
}

const PageTitleContext = createContext<PageTitleContextType | undefined>(undefined);

export const usePageTitle = () => {
  const context = useContext(PageTitleContext);
  if (!context) {
    throw new Error('usePageTitle must be used within a PageTitleProvider');
  }
  return context;
};

interface PageTitleProviderProps {
  children: ReactNode;
}

export const PageTitleProvider: React.FC<PageTitleProviderProps> = ({ children }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState<string | undefined>();
  const [customContent, setCustomContent] = useState<CustomContent | undefined>();

  const setPageTitle = useCallback((newTitle: string, newDescription?: string, newCustomContent?: CustomContent) => {
    setTitle(newTitle);
    setDescription(newDescription);
    setCustomContent(newCustomContent);
  }, []);

  return (
    <PageTitleContext.Provider value={{ title, description, customContent, setPageTitle }}>
      {children}
    </PageTitleContext.Provider>
  );
};
